import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import Tooltip from "@mui/material/Tooltip";
import images from "../../assets/images";
import Modal from "../../components/modal/Modal";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { DateSelector } from "../../components/DateSelector/DateSelector";
import RiskAssementCreation from "./RiskAssesmentCreation";
import {
  useRiskAssessment,
  usePremisesStore,
  useOrgUsersStore,
  useRegionStore,
} from "../../store/store";
import { isDefined } from "../../common/utils";
import { DotLoader } from "../../components/loader/Loader";
import { usePermissions } from "../../hooks/usePermission";
import { RISKASSES_MODULE } from "../../constants/modules";

ModuleRegistry.registerModules([AllCommunityModule]);
const gridTheme = themeQuartz.withParams({
  accentColor: "#93AA9A",
  headerHeight: "200px",
  browserColorScheme: "light",
  headerFontSize: 14,
});

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const ResponsiveListCell = ({
  value,
  onClick,
}: {
  value: string[];
  onClick?: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayCount, setDisplayCount] = useState(2);

  useEffect(() => {
    const calculateDisplayCount = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      // Assuming average character width of 8px and some padding
      const avgCharWidth = 8;
      const padding = 60; // Increased padding to account for "... +X more" text
      const availableWidth = containerWidth - padding;

      let totalChars = 0;
      let count = 0;

      for (const item of value) {
        totalChars += item.length + 2; // +2 for comma and space
        const estimatedWidth = totalChars * avgCharWidth;

        if (estimatedWidth > availableWidth) break;
        count++;
      }

      setDisplayCount(Math.max(1, count));
    };

    calculateDisplayCount();
    window.addEventListener("resize", calculateDisplayCount);
    return () => window.removeEventListener("resize", calculateDisplayCount);
  }, [value]);

  const displayText =
    value.length > displayCount
      ? `${value.slice(0, displayCount).join(", ")}... +${value.length - displayCount
      } more`
      : value.join(", ");

  return (
    <div
      ref={containerRef}
      className="relative cursor-pointer text-center p-2 text-gray-600"
      onClick={() => onClick && value.length > displayCount && onClick()}
    >
      {displayText}
    </div>
  );
};

const AssessorCell = ({ value, data }: { value: string[]; data: any }) => {
  const { orgUsersData } = useOrgUsersStore();

  const assessorNames = value.map((id) => {
    const user = orgUsersData.find(
      (u: { _id: string; firstName: string; lastName: string }) => u._id === id,
    );
    return user ? `${user.firstName} ${user.lastName}` : id;
  });

  const emails: string[] = Array.isArray(data?.assessorEmailAddress)
    ? data.assessorEmailAddress
    : [];

  const tooltipTitle = assessorNames.length
    ? assessorNames.map((name, i) => (
      <div key={i}>{emails[i] ? `${name}: ${emails[i]}` : name}</div>
    ))
    : "";

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <div>
        <ResponsiveListCell
          value={assessorNames}
          onClick={() => {
            if (data) {
              data.onViewClick();
            }
          }}
        />
      </div>
    </Tooltip>
  );
};

const PremisesCell = ({ value, data }: { value: string[]; data: any }) => {
  return (
    <ResponsiveListCell
      value={value}
      onClick={() => data && data.onViewClick && data.onViewClick()}
    />
  );
};

const ResponsiblePersonCell = ({
  value,
  data,
}: {
  value: string[];
  data: any;
}) => {
  return (
    <ResponsiveListCell
      value={value}
      onClick={() => data && data.onViewClick && data.onViewClick()}
    />
  );
};

const RiskAssess: React.FC = () => {
  const location = useLocation();
  const {
    allRiskAssessments,
    fetchAllRiskAssessments,
    loadingRisks,
    searchAllRiskAssessment,
    searchRiskAssessment,
    clearData,
    riskAssessment,
    deleteRiskAssessment,
  } = useRiskAssessment();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { regions, fetchRegions } = useRegionStore();
  const { canCreate, canRead, canUpdate } = usePermissions();

  const navigate = useNavigate();
  // Detect module from route
  const getModuleName = (): string | undefined => {
    const path = location.pathname;
    if (path.includes("/hazardous-materials")) {
      return "hazardous-materials";
    } else if (path.includes("/external-emergency-preparedness")) {
      return "external-emergency-preparedness";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/risk-assessment")
    ) {
      return "fire-system";
    }
    return undefined;
  };

  const moduleName = getModuleName();
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const defaultColDef = useMemo(
    () => ({
      wrapText: true,
      headerClass: "font-bold text-md bg-gray-100 p-2",
    }),
    [],
  );
  const [rowData, setRowData] = useState<any[]>([]);
  const [columnDefs, setColDefs] = useState<any>([
    {
      field: "responsiblePersonDutyHolder",
      headerName: "Responsible Person/ Duty Holder",
      sortable: true,
      minWidth: 200,
      flex: 1.5,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px" },
      cellRenderer: ResponsiblePersonCell,
    },
    {
      field: "premises",
      headerClass: "ag-center-header",
      headerStyle: { textAlign: "center", backgroundColor: "#e9f5ff" },
      flex: 1,
      headerName: "Premises",
      sortable: true,
      cellStyle: { textAlign: "center", padding: "10px" },
      cellRenderer: PremisesCell,
    },
    {
      field: "region",
      headerClass: "ag-center-header",
      headerStyle: { textAlign: "center", backgroundColor: "#e9f5ff" },
      flex: 1,
      headerName: "Region",
      sortable: true,
      cellStyle: { textAlign: "center", padding: "10px" },
      cellRenderer: PremisesCell,
    },
    {
      field: "AddressOfPremises",
      headerName: "Address of Premises",
      sortable: false,
      headerClass: "ag-center-header",
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1.5,
      cellStyle: { textAlign: "center", padding: "10px" },
    },
    {
      field: "Date",
      headerName: "Date",
      sortable: true,
      sort: "desc",
      headerClass: "ag-center-header",
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1,
      cellStyle: { textAlign: "center", padding: "10px" },
      valueFormatter: (params: any) => formatDate(params.value),
    },
    {
      field: "assessor",
      headerClass: "ag-center-header",
      headerName: "Assessors",
      headerStyle: { backgroundColor: "#e9f5ff" },
      sortable: true,
      flex: 1.5,
      cellStyle: { padding: "10px" },
      cellRenderer: AssessorCell,
    },
    {
      field: "status",
      headerName: "Status",
      headerClass: "ag-center-header",
      headerStyle: { textAlign: "center", backgroundColor: "#e9f5ff" },
      flex: 1,
      sortable: true,
      cellStyle: (params: any) => {
        let color = "black";
        switch (params.value?.toLowerCase()) {
          case "submitted":
            color = "#4CAF50";
            break;
          case "open":
            color = "#FFC107";
            break;
          case "closed":
            color = "#9E9E9E";
            break;
        }
        return {
          color: color,
          fontWeight: "bold",
          padding: "10px 0",
          textAlign: "center",
        };
      },
      valueFormatter: (params: any) => {
        if (!params.value) return "";
        const val = params.value.toLowerCase();
        if (val === "open") return "Draft";
        return params.value.charAt(0).toUpperCase() + params.value.slice(1);
      },
    },
    {
      field: "action",
      headerClass: "ag-center-header",
      headerName: "Action",
      cellStyle: { padding: "10px" },
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 0.8,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3 justify-center">
          {/* VIEW */}
          {canRead(RISKASSES_MODULE) && (
            <button
              type="button"
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              onClick={() => {
                setEditViewId(params.data.id);
                setMode("view");
              }}
            >
              <img
                className="w-5 h-5"
                src={images.viewIcon}
                alt="View"
              />
            </button>
          )}
          {/* EDIT */}
          {canUpdate(RISKASSES_MODULE) && (
            <button
              type="button"
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              onClick={() => {
                setEditViewId(params.data.id);
                setMode("edit");
              }}
            >
              <img
                className="w-5 h-5"
                src={images.editIcon}
                alt="Edit"
              />
            </button>
          )}
        </div>
      ),
      filter: false,
    },
  ]);
  const theme = useMemo(() => {
    return gridTheme;
  }, []);
  const gridRef = useRef<any>(null);
  const [editViewId, setEditViewId] = useState<string>("");
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [showCloseConfirm, setShowCloseConfirm] = useState<boolean>(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
    if (!value || value === "all") {
      setSelectedPremises("");
    }
  };
  const handlePremisesChange = (value: string) => {
    setSelectedPremises(value);
    if (!value || value === "all") {
      setSelectedRegion("");
    }
  };

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });

  useEffect(() => {
    // Use setTimeout to avoid ResizeObserver issues
    setTimeout(() => {
      gridRef.current?.api.sizeColumnsToFit();
    }, 0);
  }, []);
  const getAllAssessments = async () => {
    await fetchAllRiskAssessments(moduleName, selectedRegion, selectedPremises);
  };

  const editAssessmentOpen = (data: any) => {
    setEditViewId(data.data.id);
  };

  const resolvePremisesAndRegions = (premiseIds: string[]) => {
    const premiseObjs = premiseIds
      .map((id) => allPremises.find((p) => String(p.id) === String(id)))
      .filter(Boolean);
    const premiseNamesArr = premiseObjs.map((p: any) => p.premisesName);
    const regionIdsArr: string[] = Array.from(
      new Set(
        premiseObjs
          .map((p: any) => p?.region)
          .filter(Boolean)
          .map((id: any) => String(id)),
      ),
    );
    const regionNamesArr = regionIdsArr.map((id) => {
      const region = regions.find((r: any) => String(r._id) === String(id));
      return region?.regionName ?? id;
    });

    return {
      premiseNamesArr,
      regionIdsArr,
      regionNamesArr,
    };
  };

  const handelSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value;
    if (keyword.length === 0) {
      getAllAssessments();
      return;
    }
    searchRiskAssessment(keyword, moduleName);
    const final_result = searchAllRiskAssessment.map((item: any, i: number) => {
      const premiseIds = Array.isArray(item.premises)
        ? item.premises
        : typeof item.premises === "string" && item.premises
          ? [item.premises]
          : [];
      const { premiseNamesArr, regionIdsArr, regionNamesArr } =
        resolvePremisesAndRegions(premiseIds);

      const assessorArray = Array.isArray(item.assessor)
        ? item.assessor
        : typeof item.assessor === "string"
          ? item.assessor.split(",").filter(Boolean)
          : [];

      const emailArray = Array.isArray(item.assessorEmailAddress)
        ? item.assessorEmailAddress
        : typeof item.assessorEmailAddress === "string"
          ? item.assessorEmailAddress.split(",").filter(Boolean)
          : [];

      return {
        responsiblePersonDutyHolder: Array.isArray(item.responsiblePerson)
          ? item.responsiblePerson
          : typeof item.responsiblePerson === "string" && item.responsiblePerson
            ? [item.responsiblePerson]
            : [],
        premises: premiseNamesArr,
        premisesIds: premiseIds,
        region: regionNamesArr,
        regionIds: regionIdsArr,
        AddressOfPremises: item.addressLine1,
        assessor: assessorArray,
        Date: new Date(item.createdAt).toLocaleDateString("en-US"),
        createdAtRaw: item.createdAt,
        assessorEmailAddress: emailArray,
        location: item.location,
        likelihood: item.likelihood,
        status: item.status || "open",
        id: item._id,
      };
    });
    setRowData(final_result);
  };

  useEffect(() => {
    getAllAssessments();
    if (!allPremises.length) fetchPremises();
    if (!orgUsersData.length) fetchOrgUsers();
    if (!regions.length) fetchRegions();
  }, [moduleName, selectedRegion, selectedPremises]);

  useEffect(() => {
    if (allRiskAssessments.length > 0) {
      const final_result = allRiskAssessments.map((item: any) => {
        const premiseIds = Array.isArray(item.premises)
          ? item.premises
          : typeof item.premises === "string" && item.premises
            ? [item.premises]
            : [];
        const { premiseNamesArr, regionIdsArr, regionNamesArr } =
          resolvePremisesAndRegions(premiseIds);

        // Handle both string and array formats for backward compatibility
        const assessorArray = Array.isArray(item.assessor)
          ? item.assessor
          : typeof item.assessor === "string"
            ? item.assessor.split(",").filter(Boolean)
            : [];

        const emailArray = Array.isArray(item.assessorEmailAddress)
          ? item.assessorEmailAddress
          : typeof item.assessorEmailAddress === "string"
            ? item.assessorEmailAddress.split(",").filter(Boolean)
            : [];

        return {
          responsiblePersonDutyHolder: Array.isArray(item.responsiblePerson)
            ? item.responsiblePerson
            : typeof item.responsiblePerson === "string" &&
              item.responsiblePerson
              ? [item.responsiblePerson]
              : [],
          premises: premiseNamesArr,
          premisesIds: premiseIds,
          region: regionNamesArr,
          regionIds: regionIdsArr,
          AddressOfPremises: item.addressLine1,
          assessor: assessorArray,
          Date: item.createdAt,
          createdAtRaw: item.createdAt,
          assessorEmailAddress: emailArray,
          location: item.location,
          likelihood: item.likelihood,
          status: item.status || "open",
          id: item._id,
          onViewClick: () => {
            setEditViewId(item._id);
            setMode("view");
          },
        };
      });
      setRowData(final_result);
    }
  }, [allRiskAssessments, orgUsersData, allPremises, regions]);

  const displayedRowData = useMemo(() => {
    return rowData.filter((row) => {
      // Region Filter
      if (selectedRegion) {
        const regionIds = Array.isArray(row.regionIds)
          ? row.regionIds.map(String)
          : [];

        if (!regionIds.includes(String(selectedRegion))) {
          return false;
        }
      }
      // Premises Filter
      if (selectedPremises) {
        const premisesIds = Array.isArray(row.premisesIds)
          ? row.premisesIds.map(String)
          : [];

        if (!premisesIds.includes(String(selectedPremises))) {
          return false;
        }
      }
      // Date Filter
      if (dateRange.start && dateRange.end && row.createdAtRaw) {
        const rowDate = new Date(row.createdAtRaw);
        const start = new Date(dateRange.start);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.end);
        end.setHours(23, 59, 59, 999);

        if (rowDate < start || rowDate > end) {
          return false;
        }
      }
      return true;
    });
  }, [
    rowData,
    selectedRegion,
    selectedPremises,
    dateRange.start,
    dateRange.end,
  ]);

  const statusCounts = useMemo(() => {
    let total = 0;
    let openCount = 0;
    let submittedCount = 0;
    let closedCount = 0;

    displayedRowData.forEach((row) => {
      total++;
      const status = row.status?.toLowerCase();
      if (status === "submitted") {
        submittedCount++;
      } else if (status === "closed") {
        closedCount++;
      } else {
        openCount++;
      }
    });

    return {
      total,
      openCount,
      submittedCount,
      closedCount,
    };
  }, [displayedRowData]);

  const closeAssessmentModal = async () => {
    // A fresh "Create Risk Assessment" wizard already persists a draft
    // record to the backend as soon as step 1 is completed (see
    // RiskAssesmentForm's createRiskForm). If the user backs out before
    // finishing (status is still "open"), remove that partial record so it
    // doesn't linger in the list.
    if (
      isCreating &&
      riskAssessment?._id &&
      riskAssessment?.status === "open"
    ) {
      await deleteRiskAssessment(riskAssessment._id);
    }
    setIsCreating(false);
    setEditViewId("");
    clearData();
    getAllAssessments();
    setMode("create");
    setShowCloseConfirm(false);
  };

  const handleModalCloseAttempt = () => {
    // Nothing being created/edited if just viewing an existing assessment.
    if (mode === "view") {
      closeAssessmentModal();
      return;
    }
    setShowCloseConfirm(true);
  };

  return (
    <>
      <div className=" w-full flex flex-col px-2 py-4">
        <div className="page-header mt-20">
          <h2 className="header-title ml-2 mt-2">
            Risk Assessment & Mitigation Plan
          </h2>
          <div className="dashHeaderRight items-center flex-wrap gap-3">
            {moduleName === "external-emergency-preparedness" && (
              <button
                className="flex btn-primary roundedBt iconWithBt text-uppercase mr-4"
                onClick={() =>
                  navigate(
                    "/external-emergency-preparedness/hazard-and-vulnerability-assessment",
                  )
                }
              >
                <img src={images.addPremiessIcon} alt="" />
                <span>HAZARD AND VULNERABILITY ASSESSMENT</span>
              </button>
            )}

            {canCreate(RISKASSES_MODULE) && (
              <div className="flex items-center gap-3 flex-wrap">
                <Dropdown
                  selectedValue={selectedRegion}
                  onChange={handleRegionChange}
                  type="region"
                />
                <Dropdown
                  selectedValue={selectedPremises}
                  onChange={handlePremisesChange}
                  type="premises"
                  selectedRegion={selectedRegion}
                />
                <button
                  type="button"
                  className="flex btn-primary roundedBt iconWithBt text-uppercase items-center"
                  onClick={() => {
                    setMode("create");
                    setIsCreating(true);
                  }}
                >
                  <img src={images.addPremiessIcon} alt="" />
                  <span>CREATE RISK ASSESSMENT</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="cardNum mt-4 mb-4">
          <div className="row">
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">
                    Total Risk Assessments
                  </span>
                  <h3>{statusCounts.total}</h3>
                </div>
                <div className="cardIcon cardIconColor1">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">Draft Assessments</span>
                  <h3>{statusCounts.openCount}</h3>
                </div>
                <div className="cardIcon cardIconColor2">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">
                    Submitted Assessments
                  </span>
                  <h3>{statusCounts.submittedCount}</h3>
                </div>
                <div className="cardIcon cardIconColor3">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">
                    Closed Assessments
                  </span>
                  <h3>{statusCounts.closedCount}</h3>
                </div>
                <div className="cardIcon cardIconColor4">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tableBlock w-full ">
          <div className="card">
            <div className="tableSearchBlock d-flex justify-content-between flex-wrap gap-3">
              <div className="leftTableSearchBlock">
                <div className="input-affix ">
                  <i className="prefix-icon anticon anticon-search"></i>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    onChange={(e) => handelSearch(e)}
                  />
                </div>
              </div>

              <DateSelector
                onDateChange={(start, end) => {
                  setDateRange({ start, end });
                }}
              />
            </div>

            {/* ----- ag grid ----- */}
            <div className="ag-theme-alpine w-full pl-2 border-0 rounded-lg shadow-lg">
              {!loadingRisks ? (
                <AgGridReact
                  rowData={displayedRowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={100}
                  defaultColDef={defaultColDef}
                  pagination={true}
                  paginationPageSize={5}
                  domLayout="autoHeight"
                  getRowStyle={() => ({
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginBottom: "8px",
                    borderRadius: "4px",
                    backgroundColor: "white",
                  })}
                />
              ) : (
                <DotLoader />
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isCreating || isDefined(editViewId)}
        onClose={handleModalCloseAttempt}
        modalClass={"w-full relative risk-modal-close"}
      >
        <RiskAssementCreation
          completionCloseModal={closeAssessmentModal}
          assessmentId={isDefined(editViewId) ? editViewId : ""}
          mode={mode}
          moduleName={moduleName}
        />
      </Modal>
      <ConfirmModal
        isOpen={showCloseConfirm}
        title="Are you sure you want to close?"
        onConfirm={closeAssessmentModal}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default RiskAssess;
